import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { Product, CartItem, Order, OrderStatus, ProductOption, PaymentStatus, Coupon, ShippingSettings, Category, ProductOptionValue, TransferAccount, StoreSettings, CustomerProfile, BusinessHours, PaymentMethod, Presentation, ShippingZone, UIContent } from '@/types';
import axios from 'axios';
import { db, auth, handleFirestoreError, OperationType } from '@/firebase';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { onAuthStateChanged, User, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { toast } from 'sonner';
import { trackEvent, AnalyticsEvents } from '@/utils/analytics';
import { NotificationService } from '@/services/NotificationService';

export interface StoreContextType {
  categories: Category[];
  addCategory: (category: Category) => void;
  updateCategory: (category: Category) => void;
  deleteCategory: (categoryId: string) => void;
  reorderCategories: (categories: Category[]) => void;
  products: Product[];
  cart: CartItem[];
  orders: Order[];
  coupons: Coupon[];
  shippingSettings: ShippingSettings;
  addToCart: (
    productId: string, 
    quantity: number, 
    selectedOptions?: { optionId: string; values: string[] }[],
    selectedPresentationId?: string
  ) => void;
  removeFromCart: (cartItemId: string) => void;
  clearCart: () => void;
  addProduct: (product: Product) => void;
  updateProduct: (updatedProduct: Product) => void;
  getProduct: (id: string) => Product | undefined;
  addOrder: (orderData: Omit<Order, 'id' | 'createdAt' | 'status' | 'paymentStatus'>) => Promise<{ order: Order; paymentUrl: string | null }>;
  updateOrderStatus: (orderId: string, status: OrderStatus) => void;
  updateOrderPaymentStatus: (orderId: string, status: PaymentStatus) => void;
  updateOrderShipping: (orderId: string, cost: number, zone: string, distance?: number) => Promise<void>;
  deleteOrder: (orderId: string) => Promise<void>;
  updateShippingSettings: (settings: ShippingSettings) => Promise<void>;
  updateStock: (productId: string, newStock: number) => void;
  options: ProductOption[];
  addOption: (option: ProductOption) => void;
  updateOption: (option: ProductOption) => void;
  deleteOption: (optionId: string) => void;
  updateProductOptions: (productId: string, optionIds: string[]) => void;
  subobjects: ProductOptionValue[];
  addSubobject: (subobject: ProductOptionValue) => void;
  updateSubobject: (subobject: ProductOptionValue) => void;
  deleteSubobject: (subobjectId: string) => void;
  addCoupon: (coupon: Coupon) => void;
  updateCoupon: (coupon: Coupon) => void;
  deleteCoupon: (couponId: string) => void;
  validateCoupon: (code: string) => Coupon | undefined;
  transferAccounts: TransferAccount[];
  addTransferAccount: (account: TransferAccount) => void;
  updateTransferAccount: (account: TransferAccount) => void;
  deleteTransferAccount: (accountId: string) => void;
  uiContent: UIContent;
  updateUIContent: (content: UIContent) => Promise<void>;
  storeSettings: StoreSettings;
  updateStoreSettings: (settings: StoreSettings) => Promise<void>;
  customers: CustomerProfile[];
  user: User | null;
  userRole: 'admin' | 'editor' | null;
  isAdmin: boolean;
  isEditor: boolean;
  isRoleLoading?: boolean;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  businessHours: BusinessHours | null;
  isStoreOpen: boolean;
  updateBusinessHours: (hours: BusinessHours) => Promise<void>;
}

export const StoreContext = createContext<StoreContextType | undefined>(undefined);

const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  '0': { isOpen: false, openTime: '09:00', closeTime: '18:00' }, // Domingo
  '1': { isOpen: true, openTime: '08:00', closeTime: '20:00' },  // Lunes
  '2': { isOpen: true, openTime: '08:00', closeTime: '20:00' },  // Martes
  '3': { isOpen: true, openTime: '08:00', closeTime: '20:00' },  // Miércoles
  '4': { isOpen: true, openTime: '08:00', closeTime: '20:00' },  // Jueves
  '5': { isOpen: true, openTime: '08:00', closeTime: '20:00' },  // Viernes
  '6': { isOpen: true, openTime: '09:00', closeTime: '18:00' },  // Sábado
};

const INITIAL_ZONES: ShippingZone[] = [
  { id: 'zone_1', name: 'Zona 1 (Centro)', price: 0, neighborhoods: ['Nueva Córdoba', 'Centro', 'Alberdi'] },
  { id: 'zone_2', name: 'Zona 2 (Gral Paz/Alta Cba)', price: 1500, neighborhoods: ['General Paz', 'Alta Córdoba', 'Juniors'] },
  { id: 'zone_3', name: 'Zona 3 (Jardín/Urca/Argüello)', price: 2000, neighborhoods: ['Jardín', 'Urca', 'Argüello', 'Maipú'] },
  { id: 'zone_4', name: 'Zona 4 (Villa El Libertador)', price: 2500, neighborhoods: ['Villa El Libertador', 'Pueyrredón', 'Empalme'] },
  { id: 'zone_5', name: 'Zona 5 (Bimaco/S. Vicente)', price: 2500, neighborhoods: ['Bimaco', 'San Vicente', 'Maldonado'] },
  { id: 'zone_6', name: 'Zona 6 (Res. Horizonte)', price: 3000, neighborhoods: ['Residencial Horizonte', 'Parque Liceo'] },
  { id: 'zone_7', name: 'Zona 7 (Villa Allende/Mendiolaza)', price: 3500, neighborhoods: ['Villa Allende', 'Mendiolaza'] },
  { id: 'zone_8', name: 'Zona 8 (Falda del Carmen/La Calera)', price: 4000, neighborhoods: ['Falda del Carmen', 'La Calera'] },
  { id: 'zone_9', name: 'Zona 9 (Unquillo/Salsipuedes)', price: 4500, neighborhoods: ['Unquillo', 'Salsipuedes'] },
  { id: 'zone_10', name: 'Zona 10 (Fuera de zona)', price: 0, neighborhoods: ['Fuera de zona', 'Otro barrio'] }
];

const DEFAULT_UI_CONTENT: UIContent = {
  activeLayout: 'default',
  hero_title: "Regalos que",
  hero_subtitle: "Desayunos artesanales y regalos personalizados para sorprender a quienes más querés.",
  about_title: "Nuestra Historia",
  about_text: "Somos un emprendimiento familiar dedicado a crear momentos inolvidables.",
  corporate_title: "Regalos Empresariales",
  corporate_text: "Sorprendé a tus colaboradores y clientes con regalos únicos y personalizados.",
  custom_title: "Regalos Personalizados",
  custom_text: "Creamos la caja perfecta para esa persona especial. Vos elegís, nosotros lo hacemos realidad.",
  urgency_title: "Su cumpleaños es mañana.",
  urgency_subtitle: "Todavía llegás.",
  urgency_description: "Pedidos antes de medianoche → entrega mañana a la mañana.",
  moments_title: "Hay un Dulzayunos",
  moments_subtitle: "para eso.",
  moments_description: "Sin importar la razón — o sin razón alguna — hay una forma de hacer que mañana a la mañana alguien se sienta la persona más querida del mundo.",
  faq_title: "Todo lo que necesitás",
  faq_subtitle: "saber.",
  faq_description: "No tenemos que explicar por qué funciona. Lo hacen ellos solos cuando abren la puerta.",
  how_it_works_title: "De cero a sorpresa",
  how_it_works_subtitle: "en minutos.",
  how_it_works_description: "No hace falta planificarlo con días de anticipación. Si pedís antes de medianoche, llegamos mañana a la mañana.",
  testimonials_title: "Lo que dicen",
  testimonials_subtitle: "nuestros clientes.",
  testimonials_description: "Más de 2.400 sorpresas entregadas con amor en Córdoba."
};

const DEFAULT_SURCHARGES: Record<PaymentMethod, number> = {
  [PaymentMethod.TRANSFERENCIA]: 0,
  [PaymentMethod.TRANSFERENCIA_MP]: 0,
  [PaymentMethod.EFECTIVO]: 0,
  [PaymentMethod.TARJETA_UALA]: 15,
  [PaymentMethod.PAGOS_INTERNACIONALES]: 15,
  [PaymentMethod.LINK_MP]: 15,
  [PaymentMethod.TARJETA]: 10
};


export const StoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [subobjects, setSubobjects] = useState<ProductOptionValue[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [transferAccounts, setTransferAccounts] = useState<TransferAccount[]>([]);
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [uiContent, setUIContent] = useState<UIContent>(DEFAULT_UI_CONTENT);
  const [storeSettings, setStoreSettings] = useState<StoreSettings>({
    whatsappNumber: '5493510000000',
    whatsappMessage: 'Hola, quiero hacer un pedido...',
    contactEmail: 'contacto@dulzayunos.com',
    instagramUrl: '',
    facebookUrl: '',
    tiktokUrl: '',
    storeLocation: 'Córdoba, Argentina',
    bannerText: '',
    bannerIsActive: false,
    shippingPolicy: '',
    returnPolicy: ''
  });
  const [shippingSettings, setShippingSettings] = useState<ShippingSettings>({
    baseCost: 3000,
    pricePerKm: 1350,
    maxKmForAutoPayment: 25,
    isMercadoPagoEnabled: true,
    surcharges: DEFAULT_SURCHARGES,
    zones: INITIAL_ZONES
  });
  const [businessHours, setBusinessHours] = useState<BusinessHours | null>(null);
  const [isStoreOpen, setIsStoreOpen] = useState<boolean>(true); // optimistic: true while loading
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'editor' | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isEditor, setIsEditor] = useState<boolean>(false);
  const [isRoleLoading, setIsRoleLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      setUserRole(null);
      setIsAdmin(false);
      setIsEditor(false);
      setIsRoleLoading(false);
      return;
    }

    setIsRoleLoading(true);
    const unsubscribeRole = onSnapshot(doc(db, 'users', user.uid), (snapshot) => {
      let role: 'admin' | 'editor' | null = null;
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data && (data.role === 'admin' || data.role === 'editor')) {
          role = data.role as 'admin' | 'editor';
        }
      } else {
        const legacyAdmins = ['audisiofausto@gmail.com', 'uateventos@gmail.com'];
        if (legacyAdmins.includes(user.email || '')) {
          role = 'admin';
        }
      }
      setUserRole(role);
      setIsAdmin(role === 'admin');
      setIsEditor(role === 'editor');
      setIsRoleLoading(false);
    }, (error) => {
      console.error('Firestore Error (user role):', error);
      setIsRoleLoading(false);
    });

    return () => {
      unsubscribeRole();
    };
  }, [user]);

  useEffect(() => {
    const unsubscribeProducts = onSnapshot(collection(db, 'products'), (snapshot) => {
      const prods: Product[] = [];
      snapshot.forEach(doc => {
        const data = doc.data() as Product;
        prods.push({
          ...data,
          stock: data.stock ?? 50,
          isHidden: data.isHidden ?? false
        });
      });
      setProducts(prods);
    }, (error) => {
      console.error('Firestore Error (products):', error);
    });

    const unsubscribeCategories = onSnapshot(query(collection(db, 'categories'), orderBy('order', 'asc')), (snapshot) => {
      const cats: Category[] = [];
      snapshot.forEach(doc => cats.push(doc.data() as Category));
      setCategories(cats);
    }, (error) => {
      console.error('Firestore Error (categories):', error);
    });

    return () => {
      unsubscribeCategories();
      unsubscribeProducts();
    };
  }, []);

  useEffect(() => {
    let unsubscribeOptions = () => {};
    let unsubscribeSubobjects = () => {};
    let unsubscribeOrders = () => {};
    let unsubscribeCoupons = () => {};
    let unsubscribeShipping = () => {};
    let unsubscribeTransferAccounts = () => {};
    let unsubscribeUIContent = () => {};

    let unsubscribeStoreSettings = () => {};
    let unsubscribeCustomers = () => {};
    let unsubscribeBusinessHours = () => {};

    const timeoutId = setTimeout(() => {
      unsubscribeOptions = onSnapshot(collection(db, 'options'), (snapshot) => {
        const opts: ProductOption[] = [];
        snapshot.forEach(doc => opts.push(doc.data() as ProductOption));
        setOptions(opts);
      }, (error) => {
        console.error('Firestore Error (options):', error);
      });

      unsubscribeSubobjects = onSnapshot(collection(db, 'subobjects'), (snapshot) => {
        const subs: ProductOptionValue[] = [];
        snapshot.forEach(doc => subs.push(doc.data() as ProductOptionValue));
        setSubobjects(subs);
      }, (error) => {
        console.error('Firestore Error (subobjects):', error);
      });

      if (isAdmin || isEditor) {
        let isFirstLoad = true;
        unsubscribeOrders = onSnapshot(query(collection(db, 'orders'), orderBy('createdAt', 'desc')), (snapshot) => {
          const ords: Order[] = [];
          snapshot.forEach(doc => ords.push(doc.data() as Order));
          
          if (!isFirstLoad) {
            snapshot.docChanges().forEach((change) => {
              if (change.type === 'added') {
                NotificationService.playNewOrderSound();
              }
            });
          }
          
          setOrders(ords);
          isFirstLoad = false;
        }, (error) => {
          console.error('Firestore Error (orders):', error);
        });
      }

      unsubscribeCoupons = onSnapshot(collection(db, 'coupons'), (snapshot) => {
        const cups: Coupon[] = [];
        snapshot.forEach(doc => cups.push(doc.data() as Coupon));
        setCoupons(cups);
      }, (error) => {
        if (error.code !== 'permission-denied') console.error('Firestore Error (coupons):', error);
      });

      unsubscribeShipping = onSnapshot(doc(db, 'settings', 'shipping'), (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const loadedSurcharges = data.surcharges || {};
          const mergedSurcharges = {
            ...DEFAULT_SURCHARGES,
            ...loadedSurcharges
          };
          const zones = data.zones && data.zones.length > 0 ? data.zones : INITIAL_ZONES;
          setShippingSettings({
            ...data,
            zones,
            surcharges: mergedSurcharges
          } as ShippingSettings);

          // Preload into Firestore if missing zones array or zones is empty
          if (isAdmin && (!data.zones || data.zones.length === 0)) {
            setDoc(doc(db, 'settings', 'shipping'), {
              ...data,
              zones: INITIAL_ZONES,
              surcharges: mergedSurcharges
            }).catch(console.error);
          }
        } else if (isAdmin) {
          setDoc(doc(db, 'settings', 'shipping'), {
            baseCost: 3000,
            pricePerKm: 1350,
            maxKmForAutoPayment: 25,
            isMercadoPagoEnabled: true,
            specialCategoryId: '',
            zones: INITIAL_ZONES,
            surcharges: DEFAULT_SURCHARGES
          }).catch(console.error);
        }
      }, (error) => {
        if (error.code !== 'permission-denied') console.error('Firestore Error (shipping):', error);
      });

      unsubscribeUIContent = onSnapshot(doc(db, 'settings', 'ui_content'), (snapshot) => {
        if (snapshot.exists()) {
          setUIContent({ ...DEFAULT_UI_CONTENT, ...snapshot.data() } as UIContent);
        } else if (isAdmin) {
          setDoc(doc(db, 'settings', 'ui_content'), DEFAULT_UI_CONTENT).catch(console.error);
        }
      }, (error) => {
        if (error.code !== 'permission-denied') console.error('Firestore Error (ui_content):', error);
      });

      unsubscribeStoreSettings = onSnapshot(doc(db, 'settings', 'store'), (snapshot) => {
        if (snapshot.exists()) {
          setStoreSettings(snapshot.data() as StoreSettings);
        }
      }, (error) => {
        if (error.code !== 'permission-denied') console.error('Firestore Error (store_settings):', error);
      });

      unsubscribeBusinessHours = onSnapshot(doc(db, 'settings', 'business_hours'), (snapshot) => {
        if (snapshot.exists()) {
          setBusinessHours(snapshot.data() as BusinessHours);
        } else {
          // Doc doesn't exist yet — use defaults for all users so the store isn't perpetually "closed"
          setBusinessHours(DEFAULT_BUSINESS_HOURS);
          // Admin creates it so it persists
          if (isAdmin) {
            setDoc(doc(db, 'settings', 'business_hours'), DEFAULT_BUSINESS_HOURS).catch(console.error);
          }
        }
      }, (error) => {
        // On error (e.g. permission-denied), fall back to defaults so orders can still go through
        console.error('Firestore Error (business_hours):', error);
        setBusinessHours(DEFAULT_BUSINESS_HOURS);
      });

      if (isAdmin || isEditor) {
        unsubscribeCustomers = onSnapshot(collection(db, 'customers'), (snapshot) => {
          const custs: CustomerProfile[] = [];
          snapshot.forEach(doc => custs.push(doc.data() as CustomerProfile));
          setCustomers(custs);
        }, (error) => {
          console.error('Firestore Error (customers):', error);
        });
      }

      unsubscribeTransferAccounts = onSnapshot(collection(db, 'transferAccounts'), (snapshot) => {
        const accounts: TransferAccount[] = [];
        snapshot.forEach(doc => accounts.push(doc.data() as TransferAccount));
        setTransferAccounts(accounts);
      }, (error) => {
        if (error.code !== 'permission-denied') console.error('Firestore Error (transferAccounts):', error);
      });
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      unsubscribeOptions?.();
      unsubscribeSubobjects?.();
      unsubscribeOrders?.();
      unsubscribeCoupons?.();
      unsubscribeShipping?.();
      unsubscribeTransferAccounts?.();
      unsubscribeUIContent?.();
      unsubscribeStoreSettings?.();
      unsubscribeCustomers?.();
      unsubscribeBusinessHours?.();
    };
  }, [isAdmin, isEditor]);

  const updateStoreSettings = useCallback(async (settings: StoreSettings) => {
    try {
      await setDoc(doc(db, 'settings', 'store'), settings);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'settings');
    }
  }, []);

  const updateBusinessHours = useCallback(async (hours: BusinessHours) => {
    try {
      await setDoc(doc(db, 'settings', 'business_hours'), hours);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'settings/business_hours');
    }
  }, []);

  useEffect(() => {
    const checkOpenStatus = () => {
      // Still loading businessHours from Firebase — default to open (optimistic)
      if (!businessHours) {
        setIsStoreOpen(true);
        return;
      }
      
      const now = new Date();
      // Argentine time (GMT-3) calculation regardless of local time
      const argTime = new Date(now.getTime() - (3 * 60 * 60 * 1000));
      const day = argTime.getUTCDay(); // 0 (Sunday) to 6 (Saturday)
      const hour = argTime.getUTCHours();
      const minute = argTime.getUTCMinutes();
      
      const dayIndex = String(day);
      const dayHours = businessHours[dayIndex];
      
      if (!dayHours || !dayHours.isOpen) {
        setIsStoreOpen(false);
        return;
      }
      
      const currentMinutes = hour * 60 + minute;
      
      const [openHour, openMinute] = dayHours.openTime.split(':').map(Number);
      const [closeHour, closeMinute] = dayHours.closeTime.split(':').map(Number);
      
      const openMinutes = openHour * 60 + openMinute;
      const closeMinutes = closeHour * 60 + closeMinute;
      
      setIsStoreOpen(currentMinutes >= openMinutes && currentMinutes < closeMinutes);
    };

    checkOpenStatus();
    
    const interval = setInterval(checkOpenStatus, 60000);
    return () => clearInterval(interval);
  }, [businessHours]);

  const addCategory = useCallback(async (category: Category) => {
    try {
      await setDoc(doc(db, 'categories', category.id), category);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'categories');
    }
  }, []);

  const updateCategory = useCallback(async (category: Category) => {
    try {
      await updateDoc(doc(db, 'categories', category.id), { ...category });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'categories');
    }
  }, []);

  const deleteCategory = useCallback(async (categoryId: string) => {
    try {
      await deleteDoc(doc(db, 'categories', categoryId));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'categories');
    }
  }, []);

  const reorderCategories = useCallback(async (newCategories: Category[]) => {
    try {
      const batch = newCategories.map(cat => updateDoc(doc(db, 'categories', cat.id), { order: cat.order }));
      await Promise.all(batch);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'categories');
    }
  }, []);

  const addToCart = useCallback((
    productId: string, 
    quantity: number, 
    selectedOptions?: { optionId: string; values: string[] }[],
    selectedPresentationId?: string
  ) => {
    const product = products.find(p => p.id === productId);
    if (!product) {
      toast.error('Producto no encontrado');
      return;
    }

    // Stock Validation
    let presentation: Presentation | undefined;
    if (product.hasPresentations && product.presentations && selectedPresentationId) {
      presentation = product.presentations.find(pres => pres.id === selectedPresentationId);
    }

    setCart(prev => {
      // Find matching item in cart
      const existingIndex = prev.findIndex(item => {
        if (item.productId !== productId) return false;
        if (item.selectedPresentationId !== selectedPresentationId) return false;
        
        const optsA = item.selectedOptions || [];
        const optsB = selectedOptions || [];
        if (optsA.length !== optsB.length) return false;
        
        return optsA.every(optA => {
          const optB = optsB.find(o => o.optionId === optA.optionId);
          if (!optB) return false;
          if (optA.values.length !== optB.values.length) return false;
          return optA.values.every(val => optB.values.includes(val));
        });
      });

      if (existingIndex > -1) {
        const existingItem = prev[existingIndex];
        const newQuantity = existingItem.quantity + quantity;

        // Validate stock for total combined quantity, only if quantity is positive (incrementing)
        if (quantity > 0) {
          if (presentation) {
            if (presentation.stock !== undefined && presentation.stock !== null && presentation.stock < newQuantity) {
              toast.error('No hay stock suficiente para esta presentación');
              return prev;
            }
          } else if (product.stock !== undefined && product.stock < newQuantity) {
            toast.error('No hay stock suficiente');
            return prev;
          }
        }

        if (newQuantity <= 0) {
          return prev.filter((_, idx) => idx !== existingIndex);
        }

        const updated = [...prev];
        updated[existingIndex] = { ...existingItem, quantity: newQuantity };
        return updated;
      } else {
        if (quantity <= 0) return prev;

        // Validate stock for new item
        if (presentation) {
          if (presentation.stock !== undefined && presentation.stock !== null && presentation.stock < quantity) {
            toast.error('No hay stock suficiente para esta presentación');
            return prev;
          }
        } else if (product.stock !== undefined && product.stock < quantity) {
          toast.error('No hay stock suficiente');
          return prev;
        }

        const newItem: CartItem = {
          id: Math.random().toString(36).substr(2, 9),
          productId,
          quantity,
          selectedOptions,
          selectedPresentationId
        };
        return [...prev, newItem];
      }
    });

    // Track Analytics
    const priceToTrack = presentation ? presentation.price : product.price;
    trackEvent(AnalyticsEvents.ADD_TO_CART, {
      item_id: productId,
      item_name: product.name,
      quantity: quantity,
      price: priceToTrack,
      currency: 'ARS',
      item_variant: presentation?.name
    });
  }, [products]);

  const removeFromCart = useCallback((cartItemId: string) => {
    const itemToRemove = cart.find(item => item.id === cartItemId);
    if (itemToRemove) {
      const product = products.find(p => p.id === itemToRemove.productId);
      trackEvent(AnalyticsEvents.REMOVE_FROM_CART, {
        item_id: itemToRemove.productId,
        item_name: product?.name || 'Unknown',
        quantity: itemToRemove.quantity,
        currency: 'ARS'
      });
    }
    setCart(prev => prev.filter(item => item.id !== cartItemId));
  }, [cart, products]);

  const clearCart = useCallback(() => setCart([]), []);

  const addProduct = useCallback(async (product: Product) => {
    try {
      await setDoc(doc(db, 'products', product.id), product);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'products');
    }
  }, []);

  const updateProduct = useCallback(async (updatedProduct: Product) => {
    try {
      await setDoc(doc(db, 'products', updatedProduct.id), updatedProduct);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'products');
    }
  }, []);

  const updateStock = useCallback(async (productId: string, newStock: number) => {
    try {
      await updateDoc(doc(db, 'products', productId), { stock: newStock });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'products');
    }
  }, []);

  const getProduct = useCallback((id: string) => products.find(p => p.id === id), [products]);

  const addOrder = useCallback(async (orderData: Omit<Order, 'id' | 'createdAt' | 'status' | 'paymentStatus'>) => {
    try {
      console.log('Iniciando proceso de pedido...', orderData);
      
      // Validar stock antes de procesar el pedido
      for (const item of orderData.items) {
        const p = products.find(prod => prod.id === item.productId);
        if (!p) {
          throw new Error(`Producto no encontrado: ${item.productId}`);
        }

        if (item.selectedPresentationId && p.hasPresentations && p.presentations) {
          const pres = p.presentations.find(pres => pres.id === item.selectedPresentationId);
          if (pres && pres.stock !== undefined && pres.stock !== null && pres.stock < item.quantity) {
            throw new Error(`Stock insuficiente para la presentación ${pres.name} de ${p.name}`);
          }
        } else if (p.stock !== undefined && p.stock < item.quantity) {
          throw new Error(`Stock insuficiente para el producto ${p.name}`);
        }
      }

      const baseUrl = window.location.origin;
      
      // Llamamos al backend para crear el pedido y generar el link de pago
      console.log('Llamando al backend para crear pedido y link de pago...');
      const response = await axios.post(`${baseUrl}/api/pedidos`, orderData);
      const { order, paymentUrl } = response.data;
      
      console.log('Respuesta del backend recibida:', { orderId: order.id, hasPaymentUrl: !!paymentUrl });
      
      // Guardamos en Firestore
      console.log('Guardando pedido en Firestore...');
      await setDoc(doc(db, 'orders', order.id), order);
      console.log('Pedido guardado en Firestore exitosamente.');
      
      // Guardamos el carrito temporalmente por si el pago falla
      localStorage.setItem('pendingCart', JSON.stringify(cart));
      
      const isTestOrder = orderData.customerName.toUpperCase().includes('PRUEBA') || orderData.customerName.toUpperCase().includes('TEST');

      if (!isTestOrder) {
        // Actualizamos stock localmente y en Firestore
        console.log('Actualizando stock de productos...');
        for (const item of orderData.items) {
          const p = products.find(prod => prod.id === item.productId);
          if (!p) continue;

          if (item.selectedPresentationId && p.hasPresentations && p.presentations) {
            const updatedPresentations = p.presentations.map(pres => {
              if (pres.id === item.selectedPresentationId && pres.stock !== undefined && pres.stock !== null) {
                return { ...pres, stock: Math.max(0, pres.stock - item.quantity) };
              }
              return pres;
            });

            try {
              await updateDoc(doc(db, 'products', p.id), { presentations: updatedPresentations });
            } catch (stockError) {
              console.warn('No se pudo actualizar el stock de la presentación:', stockError);
            }
          } else if (p.stock !== undefined) {
            try {
              await updateDoc(doc(db, 'products', p.id), { stock: Math.max(0, p.stock - item.quantity) });
            } catch (stockError) {
              console.warn('No se pudo actualizar el stock:', stockError);
            }
          }
        }
        
        // Actualizamos stock de opciones globales seleccionadas
        console.log('Actualizando stock de opciones globales...');
        for (const item of orderData.items) {
          if (!item.selectedOptions) continue;

          for (const selOpt of item.selectedOptions) {
            const globalOpt = options.find(o => o.id === selOpt.optionId || o.name === selOpt.optionId);
            if (!globalOpt) continue;

            let hasUpdatedStock = false;
            const updatedValues = globalOpt.values.map(v => {
              if (selOpt.values.includes(v.name) || selOpt.values.includes(v.id)) {
                if (v.stock !== undefined && v.stock !== null) {
                  hasUpdatedStock = true;
                  return { ...v, stock: Math.max(0, v.stock - item.quantity) };
                }
              }
              return v;
            });

            if (hasUpdatedStock) {
              try {
                await updateDoc(doc(db, 'options', globalOpt.id), { values: updatedValues });
              } catch (optStockError) {
                console.warn(`No se pudo actualizar el stock de la opción global ${globalOpt.name}:`, optStockError);
              }
            }
          }
        }
      } else {
        console.log('Modo prueba detectado (PRUEBA/TEST en el nombre): Saltando actualización de stock.');
      }
      
      // CRM: Guardar/Actualizar Perfil de Cliente
      try {
        const customerId = orderData.customerPhone.replace(/[^0-9]/g, '');
        const customerRef = doc(db, 'customers', customerId);
        
        // Al estar en el frontend, usamos setDoc con merge
        // En producción idealmente esto iría en una Cloud Function o en el backend.
        const currentTotal = order.total || 0;
        await setDoc(customerRef, {
          id: customerId,
          name: orderData.customerName,
          phone: orderData.customerPhone,
          email: '', // Añadiremos email en el checkout pronto
          lastOrderDate: order.createdAt,
        }, { merge: true });
        
        // Incrementar métricas usando una función transaccional de Firebase no es posible sin leer primero,
        // Pero para el propósito de este demo simple de CRM, guardamos la última orden.
        console.log('CRM: Cliente actualizado', customerId);
      } catch (crmError) {
        console.warn('Error al actualizar CRM de cliente', crmError);
      }
      
      clearCart();
      
      trackEvent(AnalyticsEvents.PURCHASE, {
        transaction_id: order.id,
        value: order.total,
        currency: 'ARS',
        items: order.items.map(i => ({
          item_id: i.productId,
          item_name: i.name,
          quantity: i.quantity,
          price: i.price
        }))
      });

      return { order, paymentUrl };
    } catch (error: any) {
      console.error('Error detallado en addOrder:', error);
      
      trackEvent(AnalyticsEvents.PAYMENT_FAILED, {
        error_message: error.message || 'Unknown error'
      });
      if (error.response) {
        console.error('Datos del error del backend:', error.response.data);
        throw new Error(error.response.data.message || error.response.data.error || 'Error en el servidor al procesar el pedido');
      }
      throw error;
    }
  }, [cart, products, clearCart]);

  const updateOrderStatus = useCallback(async (orderId: string, status: OrderStatus) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), { status });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'orders');
    }
  }, []);

  const updateOrderPaymentStatus = useCallback(async (orderId: string, paymentStatus: PaymentStatus) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), { paymentStatus });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'orders');
    }
  }, []);

  const updateOrderShipping = useCallback(async (orderId: string, cost: number, zone: string, distance?: number) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const order = orders.find(o => o.id === orderId);
      if (!order) return;

      const newTotal = (order.total - (order.shippingCost || 0)) + cost;

      await updateDoc(orderRef, {
        shippingCost: cost,
        shippingZone: zone,
        distanceKm: distance,
        total: newTotal
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'orders');
    }
  }, [orders]);

  const deleteOrder = useCallback(async (orderId: string) => {
    try {
      await deleteDoc(doc(db, 'orders', orderId));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'orders');
    }
  }, []);

  const updateShippingSettings = useCallback(async (settings: ShippingSettings) => {
    try {
      await setDoc(doc(db, 'settings', 'shipping'), settings);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'settings/shipping');
    }
  }, []);

  const updateUIContent = useCallback(async (content: UIContent) => {
    try {
      await setDoc(doc(db, 'settings', 'ui_content'), content);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'settings/ui_content');
    }
  }, []);

  const addOption = useCallback(async (option: ProductOption) => {
    try {
      await setDoc(doc(db, 'options', option.id), option);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'options');
    }
  }, []);

  const addSubobject = useCallback(async (subobject: ProductOptionValue) => {
    try {
      await setDoc(doc(db, 'subobjects', subobject.id), subobject);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'subobjects');
    }
  }, []);

  const updateSubobject = useCallback(async (subobject: ProductOptionValue) => {
    try {
      await setDoc(doc(db, 'subobjects', subobject.id), subobject);
      // Update options that use this subobject
      const optionsToUpdate = options.filter(o => o.values.some(v => v.id === subobject.id));
      for (const o of optionsToUpdate) {
        const newValues = o.values.map(v => v.id === subobject.id ? subobject : v);
        await updateOption({ ...o, values: newValues });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'subobjects');
    }
  }, [options]);

  const deleteSubobject = useCallback(async (subobjectId: string) => {
    try {
      await deleteDoc(doc(db, 'subobjects', subobjectId));
      // Remove from options
      const optionsToUpdate = options.filter(o => o.values.some(v => v.id === subobjectId));
      for (const o of optionsToUpdate) {
        const newValues = o.values.filter(v => v.id !== subobjectId);
        await updateOption({ ...o, values: newValues });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'subobjects');
    }
  }, [options]);

  const updateOption = useCallback(async (updatedOption: ProductOption) => {
    try {
      await setDoc(doc(db, 'options', updatedOption.id), updatedOption);
      // Update products that use this option
      const productsToUpdate = products.filter(p => p.options?.some(o => o.id === updatedOption.id));
      for (const p of productsToUpdate) {
        const newOptions = p.options?.map(o => o.id === updatedOption.id ? updatedOption : o);
        await updateDoc(doc(db, 'products', p.id), { options: newOptions });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'options');
    }
  }, [products]);

  const deleteOption = useCallback(async (optionId: string) => {
    try {
      await deleteDoc(doc(db, 'options', optionId));
      // Remove from products
      const productsToUpdate = products.filter(p => p.options?.some(o => o.id === optionId));
      for (const p of productsToUpdate) {
        const newOptions = p.options?.filter(o => o.id !== optionId);
        await updateDoc(doc(db, 'products', p.id), { options: newOptions });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'options');
    }
  }, [products]);

  const updateProductOptions = useCallback(async (productId: string, optionIds: string[]) => {
    try {
      const selectedOptions = options.filter(o => optionIds.includes(o.id));
      await updateDoc(doc(db, 'products', productId), { options: selectedOptions });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'products');
    }
  }, [options]);

  const addCoupon = useCallback(async (coupon: Coupon) => {
    try {
      await setDoc(doc(db, 'coupons', coupon.id), coupon);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'coupons');
    }
  }, []);

  const updateCoupon = useCallback(async (coupon: Coupon) => {
    try {
      await updateDoc(doc(db, 'coupons', coupon.id), { ...coupon });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'coupons');
    }
  }, []);

  const deleteCoupon = useCallback(async (couponId: string) => {
    try {
      await deleteDoc(doc(db, 'coupons', couponId));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'coupons');
    }
  }, []);

  const addTransferAccount = useCallback(async (account: TransferAccount) => {
    try {
      await setDoc(doc(db, 'transferAccounts', account.id), account);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'transferAccounts');
    }
  }, []);

  const updateTransferAccount = useCallback(async (account: TransferAccount) => {
    try {
      await setDoc(doc(db, 'transferAccounts', account.id), account);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'transferAccounts');
    }
  }, []);

  const deleteTransferAccount = useCallback(async (accountId: string) => {
    try {
      await deleteDoc(doc(db, 'transferAccounts', accountId));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'transferAccounts');
    }
  }, []);

  const validateCoupon = useCallback((code: string) => {
    const coupon = coupons.find(c => c.code.toUpperCase() === code.toUpperCase() && c.isActive);
    if (!coupon) return undefined;
    
    // Check expiry
    if (coupon.expiryDate) {
      const expiry = new Date(coupon.expiryDate);
      if (expiry < new Date()) return undefined;
    }
    
    return coupon;
  }, [coupons]);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  const contextValue = useMemo(() => ({
    categories,
    addCategory,
    updateCategory,
    deleteCategory,
    reorderCategories,
    products,
    cart,
    orders,
    coupons,
    addToCart,
    removeFromCart,
    clearCart,
    addProduct,
    updateProduct,
    getProduct,
    addOrder,
    updateOrderStatus,
    updateOrderPaymentStatus,
    updateOrderShipping,
    deleteOrder,
    updateShippingSettings,
    updateStock,
    options,
    addOption,
    updateOption,
    deleteOption,
    updateProductOptions,
    subobjects,
    addSubobject,
    updateSubobject,
    deleteSubobject,
    addCoupon,
    updateCoupon,
    deleteCoupon,
    validateCoupon,
    transferAccounts,
    addTransferAccount,
    updateTransferAccount,
    deleteTransferAccount,
    uiContent,
    updateUIContent,
    storeSettings,
    updateStoreSettings,
    customers,
    shippingSettings,
    user,
    userRole,
    isAdmin,
    isEditor,
    isRoleLoading,
    loginWithGoogle,
    logout,
    businessHours,
    isStoreOpen,
    updateBusinessHours
  }), [
    categories, addCategory, updateCategory, deleteCategory, reorderCategories,
    products, cart, orders, coupons, addToCart, removeFromCart, clearCart,
    addProduct, updateProduct, getProduct, addOrder, updateOrderStatus,
    updateOrderPaymentStatus, updateOrderShipping, deleteOrder,
    updateShippingSettings, updateStock, options, addOption, updateOption,
    deleteOption, updateProductOptions, subobjects, addSubobject,
    updateSubobject, deleteSubobject, addCoupon, updateCoupon, deleteCoupon,
    validateCoupon, transferAccounts, addTransferAccount, updateTransferAccount,
    deleteTransferAccount, uiContent, updateUIContent, storeSettings, updateStoreSettings,
    customers, shippingSettings, user, userRole, isAdmin, isEditor, isRoleLoading, 
    loginWithGoogle, logout, businessHours, isStoreOpen, updateBusinessHours
  ]);

  return (
    <StoreContext.Provider value={contextValue}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) throw new Error("useStore must be used within a StoreProvider");
  return context;
};
